// features/workstations/tabs/property-dossier.js
// ═══════════════════════════════════════════════════════════
// PROPERTY DEAL DOSSIER TAB — "Signal Read for an address" for
// the Wealth Building domain. Two columns:
//   ┌─────────────┬──────────────────────────────────────┐
//   │  Sidebar    │  • Empty: AddressForm                │
//   │  (history)  │  • Analyzing: AnalyzingState          │
//   │             │  • Output: DossierOutput              │
//   └─────────────┴──────────────────────────────────────┘
// Mirrors features/workstations/tabs/signal-read.js exactly.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import AddressForm from '../property-dossier/address-form';
import AnalyzingState from '../property-dossier/analyzing-state';
import DossierOutput from '../property-dossier/dossier-output';
import HistoryList from '../property-dossier/history-list';
import InvestorProfile from '../property-dossier/investor-profile';

export default function PropertyDossierTab({ workstation, userId }) {
  const [view, setView] = useState('form'); // 'form' | 'analyzing' | 'output'
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [analyzingAddr, setAnalyzingAddr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setHistoryLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstation.id, user_id: userId });
      const res = await fetch(`/api/workstations/property-dossier?${qs}`, { cache: 'no-store' });
      const data = res.ok ? await res.json() : { dossiers: [] };
      setHistory(data.dossiers || []);
    } catch (e) {
      console.warn('[property-dossier] history load failed:', e?.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadDossier = useCallback(async (id) => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/workstations/property-dossier?id=${encodeURIComponent(id)}`, { cache: 'no-store' });
      const data = res.ok ? await res.json() : null;
      if (data?.dossier) {
        setActive(data.dossier);
        setActiveId(id);
        setView(data.dossier.status === 'analyzing' ? 'analyzing' : 'output');
      }
    } catch (e) {
      setError(e?.message || 'Could not load dossier');
    } finally {
      setBusy(false);
    }
  }, []);

  const handleSubmit = useCallback(async ({ address, deal_params }) => {
    setBusy(true);
    setError(null);
    setAnalyzingAddr(address);
    setView('analyzing');
    setActiveId(null);
    setActive(null);
    try {
      const res = await fetch('/api/workstations/property-dossier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, workstation_id: workstation.id, address, deal_params }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setActive(data.dossier);
      setActiveId(data.dossier.id);
      setView('output');
      loadHistory();
    } catch (e) {
      setError(e?.message || 'Analysis failed');
      setView('form');
    } finally {
      setBusy(false);
    }
  }, [userId, workstation?.id, loadHistory]);

  const handleDecision = useCallback(async ({ note }) => {
    if (!activeId) return;
    setBusy(true);
    try {
      const res = await fetch('/api/workstations/property-dossier', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activeId, user_id: userId, decision_note: note }),
      });
      const data = await res.json();
      if (res.ok && data.dossier) { setActive(data.dossier); loadHistory(); }
    } catch (e) {
      console.warn('[property-dossier] decision save failed:', e?.message);
    } finally {
      setBusy(false);
    }
  }, [activeId, userId, loadHistory]);

  const handleNew = useCallback(() => {
    setView('form'); setActiveId(null); setActive(null); setError(null);
  }, []);

  // ─── Remove a single dossier ──────────────────────────────
  const handleDelete = useCallback(async (id) => {
    if (!id) return;
    try {
      await fetch(`/api/workstations/property-dossier?id=${encodeURIComponent(id)}&user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('[property-dossier] delete failed:', e?.message);
    }
    if (id === activeId) { setView('form'); setActiveId(null); setActive(null); }
    loadHistory();
  }, [userId, activeId, loadHistory]);

  // ─── Clear all history ────────────────────────────────────
  const handleClearAll = useCallback(async () => {
    if (!history.length) return;
    if (typeof window !== 'undefined' && !window.confirm('Remove all saved analyses for this workstation?')) return;
    await Promise.all(history.map((it) =>
      fetch(`/api/workstations/property-dossier?id=${encodeURIComponent(it.id)}&user_id=${encodeURIComponent(userId)}`, { method: 'DELETE' }).catch(() => {})
    ));
    setView('form'); setActiveId(null); setActive(null);
    loadHistory();
  }, [history, userId, loadHistory]);

  if (!workstation || !userId) {
    return (
      <div className="ws-tab-content">
        <div className="ws-placeholder">
          <div className="ws-placeholder-eyebrow">Loading</div>
          <div className="ws-placeholder-title">Deal Dossier</div>
          <div className="ws-placeholder-body">Preparing the property analyzer…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-tab-content">
      <div className="ws-sectioned-header">
        <div className="ws-sectioned-title-block">
          <div className="ws-sectioned-eyebrow">Workstation</div>
          <div className="ws-sectioned-title">Deal Dossier</div>
          <div className="ws-sectioned-subtitle">
            Enter an address + your deal — comps, ARV, deal math, and a tax-strategy read for your situation.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, padding: '0 24px 80px', alignItems: 'flex-start' }}>
        <HistoryList
          items={history}
          activeId={activeId}
          onSelect={loadDossier}
          onNew={handleNew}
          onDelete={handleDelete}
          onClearAll={handleClearAll}
          loading={historyLoading}
        />

        <main style={{ flex: 1, minWidth: 0 }}>
          <InvestorProfile workstation={workstation} userId={userId} />

          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, background: 'rgba(196,96,80,0.12)', border: '1px solid #c46050', color: '#c46050', fontSize: 13 }}>
              {error}
            </div>
          )}

          {view === 'form' && <AddressForm onSubmit={handleSubmit} busy={busy} />}
          {view === 'analyzing' && <AnalyzingState address={analyzingAddr} />}
          {view === 'output' && active && <DossierOutput dossier={active} onDecision={handleDecision} busy={busy} />}
          {view === 'output' && !active && busy && <div className="ws-loading">Loading dossier…</div>}
        </main>
      </div>
    </div>
  );
}
